import {hasSizesLow, hasSizesOos} from '~/utils/stock';
import {SINGLES_THRESHOLD} from '~/constants/QuoteConstants';

function roundedPrice(price) {
  return (Math.round(100 * parseFloat(price)) / 100)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, '$&,');
}

export function formatPrice(price) {
  return '$' + roundedPrice(price);
}

export const getUnitPrice = (price = '', quantity = 0) => {
  const priceFloat = parseFloat(price.replace(/[$,]/g, ''));
  return formatPrice(priceFloat / quantity);
};

// add leading zero to some items. this should be removed once ndx supports product catalog
export function ensureLeadingZero(key) {
  return parseInt(key, 10) < 10000 ? `0${key}` : key;
}

// Format a decimal into a rounded percentage
export function formatPercentage(pctAsDecimal) {
  return Math.round(pctAsDecimal * 100) + '%';
}

// Format a plus size string into an abbreviated form
// E.g. formatPlusSize('XXXL') => '3XL'
export function formatPlusSizes(plusSize) {
  let size = plusSize;
  const splitX = plusSize.split('X');
  const numX = splitX.length - 1;
  if (/^XX.*L(T*)$/.test(size)) {
    size = numX + 'X' + splitX[numX];
  }

  return size;
}

export function ifPlusSize(sizeLabel) {
  return sizeLabel === 'PLUS';
}

export const isBulk = quantity => quantity >= 6;

export const isSingles = quantity => quantity < SINGLES_THRESHOLD;

export function incrementPriceByPercentage(price, percentage) {
  const incrementedPrice = price + price * (percentage / 100);
  return roundedPrice(incrementedPrice);
}

export const unavailableSize = unavailable =>
  unavailable ? 'unavailable-size' : '';

// Get a relative url path for a sizing lineup link
export function getSizingLineupUrl(styleId) {
  return `/items/sizing/${ensureLeadingZero(styleId)}_lineup/standard.htm`;
}

// Get a relative url path for a sizing chart link
export function getSizingChartUrl(styleId) {
  return `/catalog/size_chart?styleno=${styleId}`;
}

export function initItems(
  items,
  {itemizedPrices, nameCount, numberCount, quantities},
  isPendingItem
) {
  if (!items || !itemizedPrices) return null;

  return items.map(item => {
    const {color, selection, style, visible} = item;
    const productId = selection.colorId;
    const itemizedPrice = itemizedPrices[productId] || {};
    const sizes = quantities[productId] || {};
    const quantity = Object.keys(sizes).reduce(
      (sum, key) => sum + sizes[key],
      0
    );
    const selectedColor = style.colors.filter(
      color => color.id === productId
    )[0];

    return {
      productId,
      productColor: color && color.name,
      productName: style && style.name,
      styleId: selection.styleId,
      sizes: quantities[productId],
      color,
      numberCount,
      nameCount,
      isPendingItem,
      quantity,

      sizingLineupUrl: getSizingLineupUrl(selection.styleId),
      sizingChartUrl: getSizingChartUrl(selection.styleId),

      // Assumed not needed
      // this.proofUrls = this.getProofUrls(this.productId);

      // Still needed?
      // this.sizeAndQuantityUnitPrice = params.sizeAndQuantityUnitPrice;

      unitPrice: itemizedPrice.itemPrice,
      totalPrice: itemizedPrice.subtotal,

      colorLimit: style.color_limit,
      categoryId: style.primary_category_id,
      categoryName: style.primary_category_name,
      hasStockIssues:
        hasSizesLow(selectedColor.sizesWithStatus) ||
        hasSizesOos(selectedColor.sizesWithStatus),
      isSpecialty: style.specialty,
      minQty: color.minimum_quantity,
      sellBlank: style.allow_blank,
      sizeLineup: style.has_lineup,
      sizeChart: style.show_sizing_chart,
      allowSponsorship: style.sponsorship,
      sizesWithStatus: selectedColor.sizesWithStatus,
      limitedSizes: color.missing_sizes,
      styleSizes: style.sizes,
      visible: visible
    };
  });
}
/**
 * Returns the first view with more than three colors
 */
export function viewWithMoreThanThreeColors(colorCounts) {
  return Object.keys(colorCounts).find(view => colorCounts[view] >= 3);
}

/**
 * Returns the view with the highest color count as {view, count}
 * i.e. {front: 4, back: 2} -> {view: 'front', count: 4}
 */
export function highestColorCountView(colorCounts = {}) {
  return Object.keys(colorCounts).reduce((accum, view) => {
    if (colorCounts[view] > (accum.count || 0)) {
      return {view, count: colorCounts[view]};
    }
    return accum;
  }, {});
}

/**
 * Returns the view with the lowest color count as {view, count}
 * i.e. {front: 4, back: 2} -> {view: 'back', count: 2}
 */
export function lowestColorCountView(colorCounts = {}) {
  return Object.keys(colorCounts).reduce((accum, view) => {
    if (colorCounts[view] < (accum.count || Infinity)) {
      return {view, count: colorCounts[view]};
    }
    return accum;
  }, {});
}

export function isCanadianQuote(postalCode) {
  return /^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$/.test(
    postalCode
  );
}

export function validUSPostalCode(postalCode) {
  return /^[0-9]{5}(?:-[0-9]{4})?$/.test(postalCode);
}

export function validUSorCanadianPostalCode(postalCode) {
  return validUSPostalCode(postalCode) || isCanadianQuote(postalCode);
}

// Convert Days into Weeks if days are divisible by 7
export const daysOrWeeks = days => {
  return days % 7 === 0 ? `${days / 7}-Week` : `${days}-Day`;
};

export const numberToWord = number => {
  switch (number) {
    case 1:
      return 'one';
    case 2:
      return 'two';
    default:
      return '';
  }
};

/**
 * Returns a colorCounts object with zero color views removed
 */
export function getNumberOfProductViewsWithColor({colorCounts}) {
  if (!colorCounts || !Object.keys(colorCounts).length === 0) return 0;
  return Object.keys(colorCounts).filter(view => colorCounts[view] > 0).length;
}

export function getCombinedSleeveColorCounts({colorCounts}) {
  if (!colorCounts) return {};

  if (colorCounts.leftSleeve || colorCounts.rightSleeve) {
    const leftColors = colorCounts.leftSleeve || 0;
    const rightColors = colorCounts.rightSleeve || 0;

    colorCounts['sleeve'] = [leftColors, rightColors];

    delete colorCounts['leftSleeve'];
    delete colorCounts['rightSleeve'];
  }

  return colorCounts;
}

// tbh I'm still confused why there are two different ways to denote a sleeve view
export function removeView({views, removeViewName}) {
  if (!views || !removeViewName) {
    return null;
  }

  // eslint-disable-next-line no-unused-vars
  const {[removeViewName]: _removedView, ...remainingViews} = views;

  if (removeViewName === 'leftSleeve' || removeViewName === 'rightSleeve') {
    if (Array.isArray(views.sleeve)) {
      const sleeveArray = [...views.sleeve];
      return {
        ...remainingViews,
        sleeve:
          removeViewName === 'leftSleeve'
            ? [0, sleeveArray[1]] // zero out left sleeve
            : [sleeveArray[0], 0] // zero out right sleeve
      };
    }
  }

  return remainingViews;
}

// Return 0 for "Jan", 2 for "Mar", 11 for "Dec"
const getMonthIndex = month => {
  return new Date(month + ' 1, 2000').getMonth();
};

/*
 * Create a Date object from a string in a format "Monday, Mar. 25th"
 */
export function createDateFromString(dateStr) {
  // Assuming the year is not specified in the string, you might want to add the current year
  // or any specific year you're working with. This example uses the current year.
  const year = new Date().getFullYear();

  // Remove the day suffix like 'th', 'nd', 'rd', 'st'
  dateStr = dateStr.replace(/(\d+)(st|nd|rd|th)/, '$1');

  // Split the string to remove the day of the week (e.g., "Monday, ") and keep "Mar. 25"
  const parts = dateStr.split(', ');
  if (parts.length > 1) {
    dateStr = parts[1];
  }

  // Remove the period after the month abbreviation to comply with recognized date formats
  dateStr = dateStr.split('.');

  const monthIndex = getMonthIndex(dateStr[0]);
  // Parse the date string into a Date object
  const date = new Date(year, monthIndex, dateStr[1]);

  // Check for Invalid Date
  if (isNaN(date)) {
    throw new Error('Invalid date format');
  }

  return date;
}

export function advanceDateToWeekdayByWeekdays(dateStr, days) {
  const date = createDateFromString(dateStr);
  // Check if the date is Saturday or Sunday and adjust accordingly
  if (date.getDay() === 6) {
    // Saturday
    date.setDate(date.getDate() + 2); // Move to Monday
  } else if (date.getDay() === 0) {
    // Sunday
    date.setDate(date.getDate() + 1); // Move to Monday
  }

  // Advance the date by the specified number of weekdays
  for (let i = 0; i < days; i++) {
    date.setDate(date.getDate() + 1); // Move to the next day

    // If the next day is Saturday, add two more days to skip to Monday
    if (date.getDay() === 6) {
      date.setDate(date.getDate() + 2);
    }
  }

  return date;
}

export function formatDateToDateStr(date) {
  const options = {
    weekday: 'long',
    month: 'short',
    day: 'numeric'
  };
  const dateTimeFormat = new Intl.DateTimeFormat('en-US', options);
  const parts = dateTimeFormat.formatToParts(date);

  return `${parts[0].value}, ${parts[2].value}. ${parts[4].value}`;
}
